import React, { createContext, useState, useEffect, useContext, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom'; // useNavigate import ediliyor
import { fetchWrapper } from '../../shared/api/fetchWrapper';
import { AuthContext } from './AuthContext';

export const TicketContext = createContext();

export const TicketProvider = ({ children }) => {
  const [tickets, setTickets] = useState([]);
  const [filterStatus, setFilterStatus] = useState(1); 
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventCache, setEventCache] = useState({}); // Event cache: { ticketId: events[] }
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true); 

  const [userCommandInput, setUserCommandInput] = useState({});
  const [allFields, setAllFields] = useState();
  // Checkbox modunda seçilen satırların SendBox'a iletilmesi için
  const [pendingCheckboxSelection, setPendingCheckboxSelection] = useState(null);
  // { FNAME: string, values: string[], rawRows: object[], columnKey: any, eventId: string }
  const [rightPanelToolbarElements, setRightPanelToolbarElements] = useState([]);
  const [memberList, setMemberList] = useState([]); // New state for member_list
  const [currentInputField, setCurrentInputField] = useState(null); // new_request'ten gelen input field bilgisi
  const [errorMessage, setErrorMessage] = useState(''); // Hata mesajı durumu
  const [currentFlow, setCurrentFlow] = useState('');
  const [flows, setFlows] = useState();
  const [runIndex, setRunIndex] = useState();
  const [develeopmentBrowser, setDeveleopmentBrowser] = useState(false);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [isSendingEvent, setIsSendingEvent] = useState(false); // SendEvent sırasında UI'ı kilitlemek için
  const [isSwitchingTicket, setIsSwitchingTicket] = useState(false);
  const [currentTicketFromCache, setCurrentTicketFromCache] = useState(false);
  const sendPerformanceRef = useRef([]);
  const switchTimeoutRef = useRef(null);
  const navigate = useNavigate(); // navigate burada kullanılıyor
  const { isLoggingIn, setIsLoggingIn } = useContext(AuthContext); // AuthContext'ten isLoggingIn'i al
  
  const fetchTickets = useCallback(async () => {
    const token = localStorage.getItem('token');
    const ticketProcessId = process.env.REACT_APP_TICKET_PROCESS;
    if (!ticketProcessId) {
      setLoading(false);
      setIsLoggingIn(false);
      return;
    }

    try {
      const fetchFunction = window.enhancedFetchWrapper || fetchWrapper;
      const response = await fetchFunction(ticketProcessId, { token: token, customer_id: 1, filter_status: filterStatus }, navigate);

      if (response && response.tickets) {
        setTickets(response.tickets);
        setLoading(false);
        setIsLoggingIn(false);
      } else {
        setLoading(false);
        setIsLoggingIn(false);
      }
    } catch (error) {
      // Config errors (missing env vars) are already warned once at module load by fetchWrapper.
      if (!error.message?.includes('REACT_APP_')) {
        console.error('❌ fetchTickets hatası:', error);
      }
      setLoading(false);
      setIsLoggingIn(false);
    }
  }, [filterStatus, navigate]);

  const fetchFlows = useCallback(async () => {
    const token = localStorage.getItem('token');
    const flowsProcessId = process.env.REACT_APP_FLOWS_PROCESS;
    if (!flowsProcessId) return;
    try {
      const fetchFunction = window.enhancedFetchWrapper || fetchWrapper;
      const response = await fetchFunction(flowsProcessId, { token: token, customer_id: 9 }, navigate);
      
      if (response && response.flows) { // Ensure "flows" is correctly referenced
        setFlows(response.flows); // Set the fetched flows
      }
    } catch (error) {
      console.error('Failed to fetch flows:', error);
    }
  }, [navigate]); 

  const markSendPerformanceStage = useCallback((ticketId, stage, detail = {}) => {
    if (!ticketId) {
      return;
    }

    const now = typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
    const allowedStages = new Set([
      'sendStart',
      'apiResponse',
      'apiError',
      'wsMessage',
      'wsFetched',
      'rendered',
    ]);

    if (!allowedStages.has(stage)) {
      console.warn(`[SendPerf] Geçersiz aşama "${stage}" atlandı.`);
      return;
    }

    const metrics = sendPerformanceRef.current.slice();

    if (stage === 'sendStart') {
      const metric = {
        ticketId,
        startedAt: now,
        stages: [{ stage, detail, timestamp: now }],
        completed: false,
      };
      metrics.push(metric);
      sendPerformanceRef.current = metrics;
      
        
      return;
    }

    const metricIndex = metrics.findIndex(
      (m) =>
        m.ticketId === ticketId &&
        !m.completed &&
        !m.stages.some((s) => s.stage === stage)
    );

    if (metricIndex === -1) {
      return;
    }

    const metric = { ...metrics[metricIndex] };
    const prevStageTimestamp =
      metric.stages.length > 0
        ? metric.stages[metric.stages.length - 1].timestamp
        : metric.startedAt;

    metric.stages = [
      ...metric.stages,
      { stage, detail, timestamp: now },
    ];
    metric[stage] = now;

    

    if (stage === 'rendered') {
      metric.completed = true;
      metric.completedAt = now;
      const total = now - metric.startedAt;
      const stagesSummary = metric.stages.map((s, idx) => {
        const prevTs = idx === 0 ? metric.startedAt : metric.stages[idx - 1].timestamp;
        return {
          stage: s.stage,
          duration: `${(s.timestamp - prevTs).toFixed(1)}ms`,
          detail: s.detail,
        };
      });

      console.groupCollapsed(
        `%c[SendPerf][${ticketId}] toplam ${total.toFixed(1)}ms`,
        'color:#16a34a;font-weight:bold;'
      );
      console.table(stagesSummary);
      console.groupEnd();
    }

    const updatedMetrics = metrics.slice();
    updatedMetrics[metricIndex] = metric;

    const completedMetrics = updatedMetrics.filter((m) => m.completed);
    const inProgressMetrics = updatedMetrics.filter((m) => !m.completed);
    const prunedCompleted =
      completedMetrics.length > 10
        ? completedMetrics.slice(completedMetrics.length - 10)
        : completedMetrics;

    sendPerformanceRef.current = [...inProgressMetrics, ...prunedCompleted];
  }, []);

  const SendEvent = useCallback(async (inputValue, files, componentName = 'Unknown') => {
    let apiResponse = null;
    // UI'ı kilitle - spam istekleri önle
    setIsSendingEvent(true);
    
    // Loading animasyonunu başlat
    setIsLoadingEvents(true);
    setLoadingProgress(0);
    
    // Progress animasyonu
    const progressInterval = setInterval(() => {
      setLoadingProgress(prev => {
        if (prev >= 90) return prev;
        return prev + Math.random() * 15;
      });
    }, 100);
    
    const addTicketEventProcessId = process.env.REACT_APP_ADD_EVENT;
    markSendPerformanceStage(selectedTicketId, 'sendStart', {
      componentName,
      inputName: inputValue?.inputName ?? null,
    });
    const formData = new FormData();
    const token = localStorage.getItem('token');
    const processId = process.env.REACT_APP_ADD_EVENT;
    
    formData.append('token', token);
    formData.append('process', processId); 
    formData.append('TICID', selectedTicketId);
    formData.append('TDESC', inputValue.inputName ? inputValue.inputName : 'message');
    formData.append('FNAME', inputValue ? inputValue.inputName : 'message');
    
    // FVALUE: Eğer object ise JSON.stringify ile string'e çevir, boş string ise boş string olarak gönder
    let fvalue = inputValue.message !== undefined ? inputValue.message : inputValue;
    if (typeof fvalue === 'object' && fvalue !== null) {
      fvalue = JSON.stringify(fvalue);
    } else if (fvalue === null || fvalue === undefined) {
      fvalue = '';
    } else {
      fvalue = String(fvalue);
    }
    formData.append('FVALUE', fvalue);
    
    formData.append('data', inputValue.data ? inputValue.data : '');
    for (let i = 0; i < files.length; i++) {
      formData.append('EVEFILE[]', files[i]);
    }
    
    // FormData'yı console'da göstermek için objeye çevir
    const formDataObj = {};
    const fileList = [];
    for (let [key, value] of formData.entries()) {
      if (key === 'EVEFILE[]') {
        fileList.push({
          name: value.name,
          size: value.size,
          type: value.type,
          lastModified: value.lastModified
        });
        if (!formDataObj[key]) formDataObj[key] = [];
        formDataObj[key].push(value.name || 'File object');
      } else {
        formDataObj[key] = value;
      }
    }

    // Sunucuya gönderilen parametreleri console'da göster
    
    try {
      // Enhanced fetchWrapper kullan (eğer mevcutsa)
      const fetchFunction = window.enhancedFetchWrapper || fetchWrapper;
      const response = await fetchFunction(addTicketEventProcessId, formData, navigate);
      apiResponse = response;
      console.log(apiResponse)
      if (response.success) {
        markSendPerformanceStage(selectedTicketId, 'apiResponse', {
          success: true,
          responseKeys: Object.keys(response ?? {}),
        });
        setLoadingProgress(100);
        
        // Cache'i temizleme - WebSocket'ten gelecek yeni event addEventToCache ile 
        // mevcut cache'e eklenecek, eski event'ler korunacak
        // Bu sayede tüm flow'lar (FLOWID'ler) korunur ve süreç akışında görünür
        
        // selectTicket çağırmıyoruz - WebSocket'ten gelecek event'i bekliyoruz
        //await selectTicket(selectedTicketId);
        
      } else {
        markSendPerformanceStage(selectedTicketId, 'apiError', {
          success: false,
          response,
        });
      }
    } catch (error) {
      apiResponse = { success: false, error: error?.message || 'SendEvent failed' };
      markSendPerformanceStage(selectedTicketId, 'apiError', {
        message: error?.message,
      });
    } finally {
      // Loading animasyonunu durdur
      clearInterval(progressInterval);
      setTimeout(() => {
        setIsLoadingEvents(false);
        setLoadingProgress(0);
        setIsSendingEvent(false); // UI kilidini kaldır
      }, 500);
    }
    return apiResponse;
  }, [selectedTicketId, navigate, markSendPerformanceStage]);

  
  const getCategories = useCallback(async () => {
    const categoriesProcessId = process.env.REACT_APP_GET_CATEGORIES_PROCESS;
    if (!categoriesProcessId) return;
    try {
      const fetchFunction = window.enhancedFetchWrapper || fetchWrapper;
      // Note: fetchWrapper injects 'process' automatically — do not include it in the body.
      const response = await fetchFunction(categoriesProcessId, {
        token: localStorage.getItem('token'),
      }, navigate);

      if (response.result) {
        setCategories(response.data);
      }
    } catch (error) {
      // Config errors (missing env vars) are already warned once at module load by fetchWrapper.
      if (!error.message?.includes('REACT_APP_')) {
        console.error('Failed to fetch categories:', error);
      }
    }
  }, [navigate]);

  // useEffect'leri fonksiyon tanımlarından sonra ekle
  useEffect(() => {
    getCategories();
  }, [getCategories]);

  // Sayfa ilk yüklendiğinde ve filterStatus değiştiğinde ticket'ları getir
  useEffect(() => {
    setLoading(true);
    fetchTickets();
    fetchFlows();
  }, [fetchTickets, fetchFlows]);

  const selectTicket = useCallback(async (TICID) => {
    const selectTicketProcessId = process.env.REACT_APP_SELECT_TICKET;

    if (switchTimeoutRef.current) {
      clearTimeout(switchTimeoutRef.current);
      switchTimeoutRef.current = null;
    }
    setIsSwitchingTicket(true);

    // Cache'den kontrol et
    if (eventCache[TICID]) {
      const cached = eventCache[TICID];
      // Tüm event'leri tut (disabled olanlar dahil), sadece duplicate kaldır
      const uniqueEvents = cached.events.filter((event, index, self) =>
        index === self.findIndex(e => String(e.EVEID) === String(event.EVEID))
      );

      switchTimeoutRef.current = setTimeout(() => {
        switchTimeoutRef.current = null;
        setSelectedTicketId(TICID);
        setEvents(uniqueEvents);
        setRunIndex(cached.runIndex);
        setCurrentFlow(cached.currentFlow);
        setAllFields(cached.allFields);
        setMemberList(cached.memberList);
        setDeveleopmentBrowser(cached.develeopmentBrowser);
        setRightPanelToolbarElements(cached.rightPanelToolbarElements);
        if (cached.selectedTicket) setSelectedTicket(cached.selectedTicket);
        if (cached.currentInputField) setCurrentInputField(cached.currentInputField);
        else setCurrentInputField(null);
        setCurrentTicketFromCache(true);
        // After React paints the new content, hide overlay (no fade). Min visible time so spinner is seen.
        requestAnimationFrame(() => requestAnimationFrame(() => setIsSwitchingTicket(false)));
      }, 350);

      return;
    }

    // Loading animasyonunu başlat
    setIsLoadingEvents(true);
    setCurrentTicketFromCache(false);
    setLoadingProgress(0);
    // Progress animasyonu
    const progressInterval = setInterval(() => {
      setLoadingProgress(prev => {
        if (prev >= 90) return prev;
        return prev + Math.random() * 10;
      });
    }, 150);
    
    const body = {
        token: localStorage.getItem('token'),
        TICID,
    };
    setSelectedTicketId(TICID);
    
    try {
        const fetchFunction = window.enhancedFetchWrapper || fetchWrapper;
        const response = await fetchFunction(selectTicketProcessId, body, navigate);
        if (response.result) {
            // response.folders'ı doğrudan kullan
            const ticketData = {
                ...response.ticket,
                folders: response.folders || []
            };
          
            setSelectedTicket(ticketData);
            
            setAllFields(response.all_fields);
            setMemberList(response.member_list);
            setDeveleopmentBrowser(response.isActiveDevelopmentPanel);

            // response.new_request artık kullanılmıyor, event'lerin INPA_CONF'u kullanılıyor
            // Bu kısmı kaldırıyoruz çünkü artık event'lerden besleniyor 
            setRightPanelToolbarElements(response.toolbar)
            
            // 🚀 new_request'ten input field bilgisini al
            if (response.new_request) {
              setCurrentInputField(response.new_request);
            } else {
              setCurrentInputField(null);
            }

            // Eventleri EVEID'ye göre sırala ve duplicate'leri kaldır
            // Tüm event'ler tutulur (disabled olanlar dahil)
            const uniqueEvents = response.events.filter((event, index, self) =>
              index === self.findIndex(e => String(e.EVEID) === String(event.EVEID))
            );
            
            const sortedEvents = uniqueEvents.sort((a, b) => {
              const aId = parseInt(a.EVEID) || 0;
              const bId = parseInt(b.EVEID) || 0;
              return aId - bId;
            });
            
            setEvents(sortedEvents);
            setTimeout(() => setIsSwitchingTicket(false), 0);

            setRunIndex(response.run_index);
            let currentFlowId = sortedEvents[sortedEvents.length-1]
            setCurrentFlow(currentFlowId.FLOWID)
            
            // Cache'e kaydet
            const cacheData = {
              events: sortedEvents, // Sıralanmış ve unique eventler
              runIndex: response.run_index,
              currentFlow: currentFlowId.FLOWID,
              allFields: response.all_fields,
              memberList: response.member_list,
              develeopmentBrowser: response.isActiveDevelopmentPanel,
              rightPanelToolbarElements: response.toolbar,
              selectedTicket: ticketData, // selectedTicket'ı da cache'e kaydet
              currentInputField: response.new_request, // new_request'i de cache'e kaydet

              timestamp: Date.now()
            };
            
            setEventCache(prev => ({
              ...prev,
              [TICID]: cacheData
            }));
            
            setLoadingProgress(100);
        }
    } catch (error) {
        console.error('Failed to fetch ticket events:', error);
    } finally {
      // Loading animasyonunu durdur
      clearInterval(progressInterval);
      setTimeout(() => {
        setIsSwitchingTicket(false);
        setIsLoadingEvents(false);
        setLoadingProgress(0);
      }, 500);
    }
  }, [navigate, eventCache]);

  // FlowChart kayıt sonrası yeniden fetch etme
  const refreshCurrentFlow = useCallback(async () => {
    if (selectedTicketId) {
      await selectTicket(selectedTicketId);
    }
  }, [selectedTicketId, selectTicket, eventCache]);
  
  const createTicket = useCallback(async (newTicket, options = {}) => {
    const { autoSelect = true } = options;
    const createTicketProcessId = process.env.REACT_APP_CREATE_TICKET;
    try {
      const fetchFunction = window.enhancedFetchWrapper || fetchWrapper;
      const response = await fetchFunction(createTicketProcessId, newTicket, navigate);
      if (response.result) {
        if (autoSelect) {
          selectTicket(response.TICID);
        }
        fetchTickets();
        return response;
      }
    } catch (error) {
      console.error('Failed to create ticket:', error);
    }
    return false;
  }, [navigate, fetchTickets, selectTicket, eventCache]);

  // Cache yönetimi fonksiyonları
  const clearEventCache = (ticketId = null) => {
    if (ticketId) {
      setEventCache(prev => {
        const newCache = { ...prev };
        delete newCache[ticketId];
        return newCache;
      });
    } else {
      setEventCache({});
    }
  };

  // Toolbar'ı sıfırlama fonksiyonu
  const resetToolbar = () => {
    setRightPanelToolbarElements([]);
  };

  const getEventCacheInfo = () => {
    const cacheKeys = Object.keys(eventCache);
    return {
      totalCachedTickets: cacheKeys.length,
      cachedTicketIds: cacheKeys,
      cacheSize: JSON.stringify(eventCache).length
    };
  };

  // Cache'den belirli bir event'i silme fonksiyonu
  const removeEventFromCache = (ticketId, eventId) => {
    if (eventCache[ticketId]) {
      setEventCache(prev => {
        const newCache = { ...prev };
        if (newCache[ticketId]) {
          newCache[ticketId] = {
            ...newCache[ticketId],
            events: newCache[ticketId].events.filter(event => String(event.EVEID) !== String(eventId))
          };
        }
        return newCache;
      });
    }
  };

  // Event silme fonksiyonu — backend'e delete API çağrısı yapar,
  // başarılı olursa event'i listeden KALDIRMAZ; LOEKZ='X' olarak işaretler
  // (gri/tıklanamaz görünür, sıra korunur).
  const deleteEvent = useCallback(async (EVEID, TICID = null) => {
    const deleteEventProcessId = "100019343";
    const ticketId = TICID || selectedTicketId;

    const body = {
      token: localStorage.getItem('token'),
      process: deleteEventProcessId,
      EVEID: EVEID
    };

    try {
      // Optimistic update: event'i hemen disable olarak işaretle
      if (ticketId) {
        deleteEventFromCache(ticketId, EVEID);
      }

      const fetchFunction = window.enhancedFetchWrapper || fetchWrapper;
      const response = await fetchFunction(deleteEventProcessId, body, navigate);

      if (response.result) {
        return { success: true };
      } else {
        return { success: false, error: 'Event silme başarısız' };
      }
    } catch (error) {
      console.error('Event silme işlemi başarısız:', error);
      return { success: false, error: error.message };
    }
  }, [selectedTicketId, navigate, eventCache]);

  // WebSocket'ten gelen event disable fonksiyonu.
  // Event'i state'den silmez, LOEKZ='X' olarak işaretler (UI'da gri/pasif görünür).
  const deleteEventFromCache = (ticketId, eventId) => {
    // Cache'deki event'i disable olarak güncelle
    setEventCache(prev => {
      const newCache = { ...prev };
      if (newCache[ticketId]) {
        newCache[ticketId] = {
          ...newCache[ticketId],
          events: newCache[ticketId].events.map(event =>
            String(event.EVEID) === String(eventId)
              ? { ...event, LOEKZ: 'X', VISIBLE: 0 }
              : event
          )
        };
      }
      return newCache;
    });

    // Seçili ticket ise events state'ini de güncelle
    if (String(ticketId) === String(selectedTicketId)) {
      setEvents(prevEvents =>
        prevEvents.map(event =>
          String(event.EVEID) === String(eventId)
            ? { ...event, LOEKZ: 'X', VISIBLE: 0 }
            : event
        )
      );
    }
  };

  // Cache'e yeni event ekleme fonksiyonu
  const addEventToCache = (ticketId, newEvent, additionalData = null) => {
    
    setEventCache(prev => {
      const newCache = { ...prev };
      
      // Eğer bu ticket için cache yoksa, yeni cache oluştur
      if (!newCache[ticketId]) {
        newCache[ticketId] = {
          events: [],
          runIndex: [],
          currentFlow: '',
          allFields: {},
          memberList: [],
          develeopmentBrowser: false,
          rightPanelToolbarElements: [],
          selectedTicket: null,
          
          timestamp: Date.now()
        };
      }
      
      // Tüm event'leri tut (disabled olanlar dahil). LOEKZ/VISIBLE filtrelemesi yapılmaz.
      const validEvents = [...newCache[ticketId].events];

      // Event'in zaten var olup olmadığını kontrol et
      const existingEventIndex = validEvents.findIndex(event => 
        String(event.EVEID) === String(newEvent.EVEID)
      );
      
      if (existingEventIndex !== -1) {
        const existingEvent = validEvents[existingEventIndex];
        if (existingEvent && (existingEvent.LOEKZ === 'X' || existingEvent.LOEKZ === 'x' || existingEvent.VISIBLE === 0 || existingEvent.VISIBLE === '0')) {
          // skip
        } else {
          validEvents[existingEventIndex] = newEvent;
        }
      } else {
        // Yeni event ekle
        validEvents.push(newEvent);
      }
      
      const updatedEvents = validEvents;
      // Duplicate'leri kaldır ve EVEID'ye göre sırala (sayısal olarak)
      const uniqueEvents = updatedEvents.filter((event, index, self) => 
        index === self.findIndex(e => String(e.EVEID) === String(event.EVEID))
      );
      
      const sortedEvents = uniqueEvents.sort((a, b) => {
        const aId = parseInt(a.EVEID) || 0;
        const bId = parseInt(b.EVEID) || 0;
        return aId - bId;
      });
      
             // 🚀 Event güncellemesi artık TicketWebSocketProvider'da yapılıyor
       // Burada sadece cache'e kaydetme işlemi yapılıyor
      
      newCache[ticketId] = {
        ...newCache[ticketId],
        events: sortedEvents,
        // Ek verileri güncelle (eğer varsa)
        ...(additionalData && {
          ...(additionalData.toolbar && { rightPanelToolbarElements: additionalData.toolbar }),
          ...(additionalData.member_list && { memberList: additionalData.member_list }),
          ...(additionalData.all_fields && { allFields: additionalData.all_fields }),
          ...(additionalData.run_index && { runIndex: additionalData.run_index }),
          ...(additionalData.isActiveDevelopmentPanel !== undefined && { develeopmentBrowser: additionalData.isActiveDevelopmentPanel }),

          ...(additionalData.ticket && { selectedTicket: additionalData.ticket }),
          ...(additionalData.new_request && { currentInputField: additionalData.new_request })
        })
      };
      return newCache;
    });
  };

  // Context value'yu useMemo ile sarmalayalım - infinite loop'u önlemek için
  const contextValue = useMemo(() => ({
    tickets, 
    setFilterStatus, 
    createTicket, 
    setTickets,
    selectTicket,
    selectedTicket, 
    events,
    SendEvent,
    categories,
    fetchTickets,
    fetchFlows,
    refreshCurrentFlow,
    deleteEvent,
    selectedTicketId,
    userCommandInput,
    loading, 
    setLoading,
    errorMessage,
    setErrorMessage,
    setEvents,
    setSelectedTicket,
    setSelectedTicketId,
    allFields,
    setAllFields,
    rightPanelToolbarElements,
    memberList,
    currentFlow,
    flows,
    runIndex,
    develeopmentBrowser,
    isLoadingEvents,
    loadingProgress,
    isSendingEvent, // SendEvent sırasında UI kilitleme durumu
    eventCache,
    clearEventCache,
    getEventCacheInfo,
    removeEventFromCache,
    deleteEventFromCache,
    addEventToCache,
    resetToolbar,
    setRightPanelToolbarElements,
    currentInputField,
    setCurrentInputField,
    markSendPerformanceStage,
    isSwitchingTicket,
    currentTicketFromCache,
    pendingCheckboxSelection,
    setPendingCheckboxSelection,
  }), [
    tickets,
    setFilterStatus,
    createTicket,
    setTickets,
    selectTicket,
    selectedTicket,
    events,
    SendEvent,
    categories,
    fetchTickets,
    fetchFlows,
    refreshCurrentFlow,
    deleteEvent,
    selectedTicketId,
    userCommandInput,
    loading, 
    setLoading,
    errorMessage,
    setErrorMessage,
    setEvents,
    setSelectedTicket,
    setSelectedTicketId,
    allFields,
    setAllFields,
    rightPanelToolbarElements,
    memberList,
    currentFlow,
    flows,
    runIndex,
    develeopmentBrowser,
    isLoadingEvents,
    loadingProgress,
    isSendingEvent, // SendEvent sırasında UI kilitleme durumu
    eventCache,
    clearEventCache,
    getEventCacheInfo,
    removeEventFromCache,
    deleteEventFromCache,
    addEventToCache,
    resetToolbar,
    setRightPanelToolbarElements,
    currentInputField,
    setCurrentInputField,
    markSendPerformanceStage,
    isSwitchingTicket,
    currentTicketFromCache,
    pendingCheckboxSelection,
    setPendingCheckboxSelection,
  ]);

  return (
    <TicketContext.Provider value={contextValue}>
      {children}
    </TicketContext.Provider>
  );
};